import React, { useEffect, useMemo, useRef, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import dayjs, { Dayjs } from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import styled from 'styled-components';
import { ButtonIcon, Icon } from 'components';
import { theme, toRem } from 'design';
import { useKeyPressed, useMediaQuery } from 'hooks';
import { getTodayWithSiteTimezone } from 'utils';

dayjs.extend(weekday);
dayjs.extend(weekOfYear);

interface IDayPickerProps {
  customizedVisibleWeek?: IWeekView[];
  dataCy?: string;
  expandedDay?: React.ReactNode;
  isLoading?: boolean;
  onCalNavigation?: (days?: IWeekView[]) => void;
  onDayClick?: (day?: IWeekView) => void;
  startDate?: string;
  disableFuture?: boolean;
  timezone?: string;
  selectedDay?: Dayjs;
}

export interface IWeekView {
  weekday: Dayjs;
  includeCheck?: boolean;
}

const Layout = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  width: 100%;
  min-width: ${toRem(550)};
  max-width: ${toRem(theme.breakpoint.large)};
  margin: 0 auto;
  background-color: ${theme.color.white};
  border: ${toRem(1)} solid ${theme.color.neutral[200]};
  border-radius: ${toRem(8)};
  padding: ${toRem(theme.space['3xl'])};

  @media (max-width: ${theme.breakpoint.small}px) {
    padding: ${toRem(theme.space['3xl'])} ${toRem(theme.space.lg)};
    min-width: auto;
  }
`;

const Month = styled.h3`
  font-size: ${toRem(theme.font.size.lg)};
  font-weight: ${theme.font.weight.semiBold};
  text-transform: uppercase;
  padding-bottom: ${toRem(theme.space.xl)};
`;

const Week = styled.ul`
  display: flex;
  align-items: center;
  list-style: none;
  gap: ${toRem(theme.space.sm)};

  @media (max-width: ${theme.breakpoint.xsmall}px) {
    gap: ${toRem(theme.space.xs)};
  }
`;

const Day = styled.li`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: ${theme.color.neutral[50]};
  border-radius: ${toRem(8)};
  padding: ${toRem(theme.space.lg)} ${toRem(theme.space.lg)};
  width: ${toRem(80)};
  height: ${toRem(80)};
  position: relative;

  &:hover,
  &:focus {
    background: ${theme.color.neutral[200]};
    cursor: pointer;
  }

  &:focus-visible {
    outline: none;
  }

  &:active,
  &.is-active {
    background-color: ${theme.color.secondary[400]};

    p {
      color: ${theme.color.white};
    }
  }

  &.is-future {
    background-color: ${theme.color.neutral[100]};

    p {
      color: ${theme.color.neutral[400]};
    }

    &:hover,
    &:focus {
      cursor: not-allowed;
      background-color: ${theme.color.neutral[100]};
    }
  }
`;

const Weekday = styled.p`
  color: ${theme.color.neutral[500]};
  font-size: ${toRem(theme.font.size.xs)};
  font-weight: ${theme.font.weight.semiBold};
  padding-bottom: ${toRem(theme.space.sm)};
  text-transform: uppercase;
`;

const Date = styled.p`
  color: ${theme.color.neutral[900]};
  font-size: ${toRem(theme.font.size['5xl'])};
  font-weight: ${theme.font.weight.regular};
`;

const StyledButtonIcon = styled(ButtonIcon)`
  border: none;

  &:hover,
  &:focus {
    background: ${theme.color.neutral[200]};
    border-radius: ${toRem(4)};
  }

  &:disabled,
  &:disabled:hover {
    background: none;
    cursor: not-allowed;
  }
`;

const ExpandedDayInfo = styled.section`
  margin-top: ${toRem(theme.space.xl)};
  width: 100%;
`;

const CheckMarkIconBox = styled.div`
  position: absolute;
  top: ${toRem(5)};
  right: ${toRem(5)};
`;

const getMonthNameDisplay = (first: Dayjs, last: Dayjs) => (first.isSame(last, 'month') ? first.format('MMMM YYYY') : `${first.format('MMMM YYYY')} - ${last.format('MMMM YYYY')}`);

const buildWeeklyCalendarDates = (date: dayjs.Dayjs): IWeekView[] => {
  const dates: IWeekView[] = [];
  const diffDays = date.startOf('day').diff(dayjs().startOf('day'), 'day') % 7; // check if the remainder of dividing the number by 7
  const modDays = diffDays >= -3 ? diffDays : diffDays + 7; // get mod days to build the calendar

  dates.push({ weekday: date.add(-3 - modDays, 'day') });
  dates.push({ weekday: date.add(-2 - modDays, 'day') });
  dates.push({ weekday: date.add(-1 - modDays, 'day') });
  dates.push({ weekday: date.add(0 - modDays, 'day') });
  dates.push({ weekday: date.add(1 - modDays, 'day') });
  dates.push({ weekday: date.add(2 - modDays, 'day') });
  dates.push({ weekday: date.add(3 - modDays, 'day') });

  return dates;
};

const buildThreeDayRange = (centerDay: Dayjs): IWeekView[] => [{ weekday: centerDay.subtract(1, 'day') }, { weekday: centerDay }, { weekday: centerDay.add(1, 'day') }];

const getCenterDay = (days: IWeekView[]) => days[Math.floor((days?.length ?? 0) / 2)]?.weekday;

const RESPONSIVE_BREAKPOINT = 870;

const DayPicker = ({ dataCy = 'day-picker', expandedDay, isLoading, onCalNavigation, onDayClick, selectedDay, startDate, disableFuture, timezone, customizedVisibleWeek }: IDayPickerProps) => {
  const today = getTodayWithSiteTimezone(timezone).format('YYYY-MM-DD');
  const isDesktop = useMediaQuery(`(min-width: ${RESPONSIVE_BREAKPOINT + 1}px)`);

  const [visibleDays, setVisibleDays] = useState<IWeekView[]>(() => {
    const initialDay = selectedDay || dayjs();
    return isDesktop ? buildWeeklyCalendarDates(initialDay) : buildThreeDayRange(initialDay);
  });
  const [activeDay, setActiveDay] = useState<Dayjs | null>(selectedDay || null);
  const [updateDays, setUpdateDays] = useState<boolean>(true);

  const desktopVisibleDaysRef = useRef<IWeekView[] | null>(null);
  const prevIsDesktopRef = useRef<boolean>(isDesktop);
  const latestVisibleDaysRef = useRef<IWeekView[]>(visibleDays);
  const latestActiveDayRef = useRef<Dayjs | null>(activeDay);
  const latestSelectedDayRef = useRef<Dayjs | undefined>(selectedDay);

  latestVisibleDaysRef.current = visibleDays;
  latestActiveDayRef.current = activeDay;
  latestSelectedDayRef.current = selectedDay;

  // Keep internal active state aligned with the controlled prop.
  useEffect(() => {
    setActiveDay(selectedDay || null);
  }, [selectedDay]);

  useEffect(() => {
    // Preserve the latest desktop positioning so it can be restored after a responsive view.
    // Important: don't overwrite the saved desktop week *during* the responsive -> desktop transition,
    // since `visibleDays` may still be the 3-day range at that moment.
    if (isDesktop && prevIsDesktopRef.current) desktopVisibleDaysRef.current = visibleDays;
  }, [isDesktop, visibleDays]);

  useEffect(() => {
    const wasDesktop = prevIsDesktopRef.current;
    if (wasDesktop === isDesktop) return;

    const currentVisibleDays = latestVisibleDaysRef.current;
    const currentActiveDay = latestActiveDayRef.current;
    const currentSelectedDay = latestSelectedDayRef.current;

    // Transition: desktop -> responsive (3-day)
    if (wasDesktop && !isDesktop) {
      desktopVisibleDaysRef.current = currentVisibleDays;

      const nextCenter = currentActiveDay || currentSelectedDay || getCenterDay(currentVisibleDays) || dayjs();
      setVisibleDays(buildThreeDayRange(nextCenter));
    }

    // Transition: responsive (3-day) -> desktop
    if (!wasDesktop && isDesktop) {
      if (desktopVisibleDaysRef.current?.length) {
        setVisibleDays(desktopVisibleDaysRef.current);
      } else {
        const nextSeedDay = currentActiveDay || currentSelectedDay || getCenterDay(currentVisibleDays) || dayjs();
        setVisibleDays(buildWeeklyCalendarDates(nextSeedDay));
      }
    }

    prevIsDesktopRef.current = isDesktop;
  }, [isDesktop]);

  useEffect(() => {
    if (updateDays && onCalNavigation) onCalNavigation(visibleDays);
    if (!updateDays) setUpdateDays(true);
  }, [visibleDays]);

  useEffect(() => {
    if (customizedVisibleWeek?.length) {
      setUpdateDays(false);
      setVisibleDays(customizedVisibleWeek);
    }
  }, [customizedVisibleWeek]);

  const onDaySelect = (day: IWeekView) => {
    setActiveDay(day.weekday);
    onDayClick?.(day);
  };

  // Page the visible dates backward (-1) or forward (1): a 7-day week on desktop, 3-day range on mobile.
  const navigate = (direction: -1 | 1) => {
    if (!visibleDays.length) return;

    // Paging deselects the day, since the screen only loads data for the visible ones.
    setActiveDay(null);
    onDayClick?.();

    const edgeDay = visibleDays[direction === -1 ? 0 : visibleDays.length - 1].weekday;

    if (isDesktop) {
      setVisibleDays(buildWeeklyCalendarDates(edgeDay.add(direction * 4, 'day')));
    } else {
      setVisibleDays(buildThreeDayRange(edgeDay.add(direction * 2, 'day')));
    }
  };

  const onPreviousClick = () => navigate(-1);
  const onNextClick = () => navigate(1);

  const enterKeyPress = useKeyPressed((event: KeyboardEvent) => event.key === 'Enter');

  const displayDays = useMemo(
    () =>
      visibleDays.map((day: IWeekView, index: number) => {
        const isSelectedDay = activeDay && day?.weekday?.isSame(activeDay, 'day');
        const isFutureDay = disableFuture && day?.weekday?.isAfter(today, 'day');
        return isLoading ? (
          <Day key={`day-${index}-loading`}>
            <Weekday>
              <Skeleton width={50} />
            </Weekday>
            <Skeleton width={30} />
          </Day>
        ) : (
          <React.Fragment key={`day-${index}`}>
            <Day
              data-cy={`${dataCy}-day`}
              tabIndex={0}
              role="button"
              className={[isSelectedDay && 'is-active', isFutureDay && 'is-future'].filter(Boolean).join(' ')}
              onClick={() => !isFutureDay && onDaySelect(day)}
              onKeyUp={() => {
                if (enterKeyPress && !isFutureDay) onDaySelect(day);
              }}
            >
              {day.includeCheck && (
                <CheckMarkIconBox>
                  <Icon dataCy={`${dataCy}-day-checkmark`} icon="CheckmarkThick" fill={isSelectedDay ? theme.color.white : theme.color.secondary[400]} />
                </CheckMarkIconBox>
              )}
              <Weekday className="notranslate">{day.weekday.format('ddd')}</Weekday>
              <Date>{day.weekday.format('D')}</Date>
            </Day>
          </React.Fragment>
        );
      }),
    [visibleDays, activeDay, enterKeyPress, isLoading, today]
  );

  const firstDay = visibleDays[0]?.weekday;
  const lastDay = visibleDays[(visibleDays?.length ?? 0) - 1]?.weekday;

  return (
    <Layout data-cy={dataCy}>
      <Month data-cy={`${dataCy}-month`}>{isLoading || !firstDay || !lastDay ? <Skeleton width={100} /> : getMonthNameDisplay(firstDay, lastDay)}</Month>
      <Week data-cy={`${dataCy}-week-view`} aria-label={!!firstDay && !!lastDay && `Calendar view for ${firstDay.format('MMMM D YYYY')} to ${lastDay.format('MMMM D YYYY')}`}>
        <StyledButtonIcon
          data-cy={`${dataCy}-previous-week-btn`}
          aria-label={isDesktop ? 'Previous week' : 'Previous day'}
          icon="Previous"
          type="button"
          appearance="white"
          onClick={onPreviousClick}
          isLoading={isLoading}
          disabled={!!startDate && !!firstDay && firstDay.isSameOrBefore(startDate, 'day')}
        />
        {displayDays}
        <StyledButtonIcon
          data-cy={`${dataCy}-next-week-btn`}
          aria-label={isDesktop ? 'Next week' : 'Next day'}
          icon="Next"
          type="button"
          appearance="white"
          onClick={onNextClick}
          isLoading={isLoading}
          disabled={disableFuture && lastDay?.isAfter(today, 'day')}
        />
      </Week>
      {expandedDay && activeDay && <ExpandedDayInfo data-cy={`${dataCy}-expanded-day-info`}>{expandedDay}</ExpandedDayInfo>}
    </Layout>
  );
};

export default DayPicker;
